import { z } from "zod";
import { ActionStatusSchema } from "./actions.js";
import { ClinicDocumentFieldBindingSchema, ClinicDocumentSchema, DocumentCategorySchema, DocumentRequestContextSchema, DocumentSelectionDecisionSchema, GeneratedDocumentArtifactSchema, } from "./clinic-documents.js";
import { ClinicOrderSetOptionSchema, FORM_ORDER_SET_LIMITS, FormOrderSetIdSchema, LabOrderSetRefSchema, } from "./form-order-sets.js";
import { FormProposalLabelSchema, LabOrderDraftPreviewFormSchema, LabOrderDraftTestSchema, LabOrderDraftTestSourceSchema, PreparedFormEntrySchema, PreparedFormNoticesSchema, PreparedFormsIncompleteSchema, PreviewLabOrderDraftRequestSchema, UnplacedOrderTestSchema, } from "./lab-order-drafts.js";
import { MANUAL_ORDER_MAX_ATTEMPT, ManualOrderFormRefSchema, ManualOrderIdSchema, sameManualOrderForm, } from "./manual-order-marker.js";
import { ImagingOrderDestinationSelectionSchema, StructuredImagingOrderStudySchema, } from "./orders.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * "+" manual orders from clinic forms: the picker's catalogue and the in-memory previews of a
 * picked form. Reading the catalogue or previewing a form writes no data row; only signing does.
 */
export const MANUAL_ORDER_LIMITS = {
    /** Offered forms in one catalogue. */
    forms: 50,
    /** Published order sets in one catalogue. */
    orderSets: 20,
    /** Search words per offered form (tags, capability names and aliases, box labels). */
    searchTerms: 200,
    /** Forms one preview renders: the picked form, its set's forms and any a clinic rule adds. */
    previewForms: 10,
    labTests: 40,
    imagingStudies: 20,
    diagnosisCodes: 20,
    /** Typed values sent with one preview, across all of its forms. */
    manualValues: 500,
    /** Characters in one typed value. */
    manualValueChars: 10_000,
    /** Saved "+" orders listed for one visit. */
    drafts: 100,
    /**
     * The most a saved order's intent may weigh as JSON, in UTF-8 bytes. The database refuses more
     * than 64 KiB of stored text; this leaves room for the spacing it adds.
     */
    draftIntentBytes: 49_152,
};
/** What a "+" order becomes when signed: a lab order, an imaging order or a signed document. */
export const ManualOrderKindSchema = z.enum(["lab", "imaging", "document"]);
/** Lab and public-health lab forms order labs, imaging forms order imaging, the rest are documents. */
export function manualOrderKindForCategory(category) {
    switch (category) {
        case "lab":
        case "public_health_lab":
            return "lab";
        case "imaging":
            return "imaging";
        case "public_health_follow_up":
        case "work_school":
        case "other":
            return "document";
    }
}
/**
 * One form the picker offers: active, published and set up by the clinic. Never its field
 * bindings, guidance or storage. `signable` is whether it has a reviewed signature box, so a form
 * without one opens view-only. `imagingOrderable` is set on imaging forms only: `facility` when a
 * printable facility is registered for exactly this published version, else `not_set_up`.
 */
export const ManualOrderCatalogueFormSchema = z
    .object({
    documentId: IdSchema,
    name: ClinicDocumentSchema.shape.name,
    category: DocumentCategorySchema,
    kind: z.enum(["order", "document"]),
    orderKind: z.enum(["lab", "imaging"]).nullable(),
    documentVersion: z.number().int().positive(),
    documentVersionHash: Sha256Schema,
    pageCount: z.number().int().positive().max(500),
    signable: z.boolean(),
    isDefault: z.boolean(),
    description: ClinicDocumentSchema.shape.description,
    searchTerms: z.array(z.string().trim().min(1).max(240)).max(MANUAL_ORDER_LIMITS.searchTerms),
    /** The published sets that use this form, each listed in the catalogue's `orderSets`. */
    orderSetIds: z.array(FormOrderSetIdSchema).max(MANUAL_ORDER_LIMITS.orderSets),
    imagingOrderable: z.enum(["facility", "not_set_up"]).nullable(),
})
    .strict()
    .superRefine((form, context) => {
    const kind = manualOrderKindForCategory(form.category);
    if (form.kind !== (kind === "document" ? "document" : "order")) {
        context.addIssue({
            code: "custom",
            path: ["kind"],
            message: "Lab, public health lab and imaging forms are order forms; the rest are documents",
        });
    }
    if (form.orderKind !== (kind === "document" ? null : kind)) {
        context.addIssue({
            code: "custom",
            path: ["orderKind"],
            message: "The order kind must follow the form's category",
        });
    }
    if ((form.imagingOrderable !== null) !== (kind === "imaging")) {
        context.addIssue({
            code: "custom",
            path: ["imagingOrderable"],
            message: "Only imaging forms say whether they are set up for ordering",
        });
    }
    if (new Set(form.orderSetIds).size !== form.orderSetIds.length) {
        context.addIssue({
            code: "custom",
            path: ["orderSetIds"],
            message: "An order set is listed once per form",
        });
    }
});
export const ManualOrderSetFormSchema = z
    .object({
    documentId: IdSchema,
    name: ClinicDocumentSchema.shape.name,
    category: DocumentCategorySchema,
})
    .strict();
/**
 * A set's test and where it prints: `placedOn` are the set's forms that tick, write or line it;
 * `alsoPrintsOn` are the forms the administrator chose "Also print it here" on. A test placed
 * nowhere prints the way its forms print any ordered test.
 */
export const ManualOrderSetTestSchema = ClinicOrderSetOptionSchema.shape.tests.element.extend({
    placedOn: z.array(IdSchema).max(FORM_ORDER_SET_LIMITS.forms),
    alsoPrintsOn: z.array(IdSchema).max(FORM_ORDER_SET_LIMITS.forms),
});
/**
 * A published order set as the picker shows it. An unavailable set (one of its forms is missing,
 * excluded or changed since the set was saved) is listed with a plain reason and cannot be used.
 */
export const ManualOrderSetSchema = z
    .object({
    id: FormOrderSetIdSchema,
    name: ClinicOrderSetOptionSchema.shape.name,
    whenToUse: ClinicOrderSetOptionSchema.shape.whenToUse,
    forms: z.array(ManualOrderSetFormSchema).min(1).max(FORM_ORDER_SET_LIMITS.forms),
    tests: z.array(ManualOrderSetTestSchema).min(1).max(FORM_ORDER_SET_LIMITS.tests),
    available: z.boolean(),
    unavailableReason: z.string().trim().min(1).max(500).optional(),
})
    .strict()
    .superRefine((set, context) => {
    if (set.available === (set.unavailableReason !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["unavailableReason"],
            message: "An unavailable order set says why, and only an unavailable one",
        });
    }
    const formIds = new Set(set.forms.map((form) => form.documentId));
    if (formIds.size !== set.forms.length) {
        context.addIssue({
            code: "custom",
            path: ["forms"],
            message: "A form is listed once per order set",
        });
    }
    const codes = new Set();
    for (const [index, test] of set.tests.entries()) {
        if (codes.has(test.code)) {
            context.addIssue({
                code: "custom",
                path: ["tests", index, "code"],
                message: "A test is listed once per order set",
            });
        }
        codes.add(test.code);
        for (const key of ["placedOn", "alsoPrintsOn"]) {
            if (test[key].some((documentId) => !formIds.has(documentId))) {
                context.addIssue({
                    code: "custom",
                    path: ["tests", index, key],
                    message: "A set's test prints only on the set's own forms",
                });
            }
        }
    }
});
const CapabilityReasonSchema = z.string().trim().min(1).max(500);
/**
 * What a physician can do with "+" at this site. `reasons` says in plain words why a kind is off,
 * and is given only for a kind that is off.
 */
export const ManualOrderCapabilitiesSchema = z
    .object({
    documents: z.boolean(),
    lab: z.boolean(),
    imaging: z.boolean(),
    reasons: z
        .object({
        documents: CapabilityReasonSchema.optional(),
        lab: CapabilityReasonSchema.optional(),
        imaging: CapabilityReasonSchema.optional(),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((capabilities, context) => {
    for (const kind of ["documents", "lab", "imaging"]) {
        if (capabilities[kind] && capabilities.reasons?.[kind] !== undefined) {
            context.addIssue({
                code: "custom",
                path: ["reasons", kind],
                message: "A reason is given only for something that is switched off",
            });
        }
    }
});
/**
 * Everything the "+" picker needs, read once per site and searched in the browser. Order sets are
 * listed only when the clinic's sets are `ready`; `not_enabled` means the clinic has not switched
 * them on, `unavailable` that they could not be read.
 */
export const ManualOrderCatalogueSchema = z
    .object({
    siteId: IdSchema,
    forms: z.array(ManualOrderCatalogueFormSchema).max(MANUAL_ORDER_LIMITS.forms),
    orderSets: z.array(ManualOrderSetSchema).max(MANUAL_ORDER_LIMITS.orderSets),
    orderSetsState: z.enum(["ready", "not_enabled", "unavailable"]),
    capabilities: ManualOrderCapabilitiesSchema,
})
    .strict()
    .superRefine((catalogue, context) => {
    if (catalogue.orderSetsState !== "ready" && catalogue.orderSets.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["orderSets"],
            message: "Order sets are listed only when they are ready",
        });
    }
    const setIds = new Set(catalogue.orderSets.map((set) => set.id));
    if (setIds.size !== catalogue.orderSets.length) {
        context.addIssue({
            code: "custom",
            path: ["orderSets"],
            message: "An order set is listed once",
        });
    }
    const formIds = new Set();
    for (const [index, form] of catalogue.forms.entries()) {
        if (formIds.has(form.documentId)) {
            context.addIssue({
                code: "custom",
                path: ["forms", index, "documentId"],
                message: "A form is listed once",
            });
        }
        formIds.add(form.documentId);
        if (form.orderSetIds.some((id) => !setIds.has(id))) {
            context.addIssue({
                code: "custom",
                path: ["forms", index, "orderSetIds"],
                message: "A form names only order sets the catalogue lists",
            });
        }
    }
});
/**
 * `blank` opens the picked form filled from the chart with nothing ticked; `set` re-renders it
 * with the physician's ticks, lines and typed values.
 */
export const ManualOrderPreviewModeSchema = z.enum(["blank", "set"]);
/** A lab draft's test; who added it is optional, since every "+" test is the physician's. */
export const ManualOrderLabTestSchema = LabOrderDraftTestSchema.extend({
    source: LabOrderDraftTestSourceSchema.optional(),
});
/** The lab part of a "+" order as the physician has it so far. */
export const ManualOrderLabIntentSchema = z
    .object({
    tests: z.array(ManualOrderLabTestSchema).max(MANUAL_ORDER_LIMITS.labTests).default([]),
    diagnosisCodes: z
        .array(z.string().trim().min(1).max(80))
        .max(MANUAL_ORDER_LIMITS.diagnosisCodes)
        .default([]),
    /** At most one set per order. */
    orderSet: LabOrderSetRefSchema.optional(),
    /** Tests suggested by clinic instructions that the physician chose not to order. */
    declinedConceptIds: PreviewLabOrderDraftRequestSchema.shape.declinedConceptIds,
    /** Keys of ordered tests the physician added to the forms by hand. */
    placedTestKeys: PreviewLabOrderDraftRequestSchema.shape.placedTestKeys,
})
    .strict();
/** A requested study as an imaging proposal carries it (ProposedEncounterImagingOrderSchema). */
export const ManualOrderImagingStudySchema = StructuredImagingOrderStudySchema.omit({
    source: true,
});
/** The imaging part of a "+" order as the physician has it so far. Studies may be incomplete. */
export const ManualOrderImagingIntentSchema = z
    .object({
    studies: z
        .array(ManualOrderImagingStudySchema)
        .max(MANUAL_ORDER_LIMITS.imagingStudies)
        .default([]),
    diagnosisCodes: z
        .array(z.string().trim().min(1).max(80))
        .max(MANUAL_ORDER_LIMITS.diagnosisCodes)
        .default([]),
    /** The registered printable facility for the picked form, when there is one. */
    destination: ImagingOrderDestinationSelectionSchema.optional(),
})
    .strict();
/** `<documentId>:<fieldId>`: which form, and which field on it, a typed value belongs to. */
export const MANUAL_ORDER_VALUE_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:[a-z][a-z0-9_.-]{2,79}$/u;
export function manualOrderValueKey(documentId, fieldId) {
    return `${documentId}:${fieldId}`;
}
/** Typed values keyed by form and field, as a lab draft's manual values are. */
export const ManualOrderManualValuesSchema = z
    .record(z.string().regex(MANUAL_ORDER_VALUE_KEY_PATTERN, "Key typed values by form and field"), z.string().max(MANUAL_ORDER_LIMITS.manualValueChars))
    .refine((values) => Object.keys(values).length <= MANUAL_ORDER_LIMITS.manualValues, `At most ${MANUAL_ORDER_LIMITS.manualValues} typed values can be sent at once`);
/**
 * Renders a "+" order's form in memory: nothing is recorded but the audit event. The encounter
 * comes from the route path. `lab` goes only with kind lab and `imaging` only with kind imaging; a
 * blank preview ticks nothing.
 */
export const ManualOrderPreviewRequestSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema,
    manualOrderId: ManualOrderIdSchema,
    kind: ManualOrderKindSchema,
    form: ManualOrderFormRefSchema,
    mode: ManualOrderPreviewModeSchema,
    lab: ManualOrderLabIntentSchema.optional(),
    imaging: ManualOrderImagingIntentSchema.optional(),
    manualValues: ManualOrderManualValuesSchema.default({}),
})
    .strict()
    .superRefine((request, context) => {
    if (request.lab !== undefined && request.kind !== "lab") {
        context.addIssue({
            code: "custom",
            path: ["lab"],
            message: "Lab tests go only with a lab order",
        });
    }
    if (request.imaging !== undefined && request.kind !== "imaging") {
        context.addIssue({
            code: "custom",
            path: ["imaging"],
            message: "Imaging studies go only with an imaging order",
        });
    }
    if (request.mode === "blank" && (request.lab?.tests.length ?? 0) > 0) {
        context.addIssue({
            code: "custom",
            path: ["lab", "tests"],
            message: "A blank form has no tests ticked",
        });
    }
    if (request.mode === "blank" && (request.imaging?.studies.length ?? 0) > 0) {
        context.addIssue({
            code: "custom",
            path: ["imaging", "studies"],
            message: "A blank form has no studies ticked",
        });
    }
    const seen = new Set();
    for (const [index, test] of (request.lab?.tests ?? []).entries()) {
        const key = `${test.codeSystem}\0${test.code}`;
        if (seen.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["lab", "tests", index],
                message: "An order cannot list the same coded test twice",
            });
        }
        seen.add(key);
    }
    const destination = request.imaging?.destination;
    if (destination !== undefined && !sameManualOrderForm(destination.form, request.form)) {
        context.addIssue({
            code: "custom",
            path: ["imaging", "destination", "form"],
            message: "The imaging facility must print the picked form",
        });
    }
});
/** The chart records the preview was filled from, by exact version (as imaging previews give). */
export const ManualOrderPreviewSourcesSchema = DocumentRequestContextSchema.pick({
    patient: true,
    encounter: true,
    provider: true,
    clinic: true,
});
/** A required field the preview left empty, so the viewer can point at it. */
export const ManualOrderMissingFieldSchema = z
    .object({
    documentId: IdSchema,
    fieldId: ClinicDocumentFieldBindingSchema.shape.fieldId,
})
    .strict();
/**
 * A test a clinic instruction suggests for this order, returned as data for the physician to add
 * or decline (declinedConceptIds). `label` is its plain name; `test` its code when it has one.
 */
export const ManualOrderProposalSchema = z
    .object({
    conceptId: FormProposalLabelSchema.shape.conceptId,
    label: FormProposalLabelSchema.shape.label.optional(),
    test: z
        .object({
        codeSystem: z.enum(["LOINC", "local", "RadLex"]),
        code: z.string().trim().min(1).max(160),
        display: z.string().trim().min(1).max(500),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * A "+" order's forms rendered as one PDF, forms in order, shaped like a lab draft's set preview so
 * the viewer reads it the same way. The picked form is always one of them. `notices`,
 * `omittedValues` and `incomplete` mean what they mean there; `missingRequired`, `unplacedTests`
 * and `proposals` are data for the order's row, never refusals. `encounterVersion` is the visit
 * version the chart values were read at; `signable` whether every form has a reviewed signature
 * box.
 */
export const ManualOrderPreviewSchema = z
    .object({
    manualOrderId: ManualOrderIdSchema,
    form: ManualOrderFormRefSchema,
    pdfBase64: z.string().min(1),
    forms: z.array(LabOrderDraftPreviewFormSchema).min(1).max(MANUAL_ORDER_LIMITS.previewForms),
    sources: ManualOrderPreviewSourcesSchema.optional(),
    encounterVersion: z.number().int().positive(),
    signable: z.boolean(),
    notices: PreparedFormNoticesSchema,
    omittedValues: z.array(PreparedFormEntrySchema).max(200),
    missingRequired: z.array(ManualOrderMissingFieldSchema).max(500),
    unplacedTests: z.array(UnplacedOrderTestSchema).max(MANUAL_ORDER_LIMITS.labTests),
    proposals: z.array(ManualOrderProposalSchema).max(100),
    incomplete: PreparedFormsIncompleteSchema.optional(),
})
    .strict()
    .superRefine((preview, context) => {
    const documentIds = new Set(preview.forms.map((form) => form.document.documentId));
    if (documentIds.size !== preview.forms.length) {
        context.addIssue({
            code: "custom",
            path: ["forms"],
            message: "A form is rendered once per preview",
        });
    }
    if (!preview.forms.some((form) => sameManualOrderForm(form.document, preview.form))) {
        context.addIssue({
            code: "custom",
            path: ["forms"],
            message: "The picked form is always part of the preview",
        });
    }
    if (preview.missingRequired.some((field) => !documentIds.has(field.documentId))) {
        context.addIssue({
            code: "custom",
            path: ["missingRequired"],
            message: "A missing field belongs to a previewed form",
        });
    }
});
/* ---- Saved "+" orders ----------------------------------------------------
 *
 * Save keeps an unsigned "+" order on the server, so it survives a reload or a closed tab and can
 * be picked up again: one row per order, keyed by the order's own id and replaced in place under
 * its version on every Save. A saved order is never a clinical order; signing still runs the
 * order or document path, and only then is the row marked signed with what that produced.
 */
/** The diagnosis an order is placed for: its code, and its name when known. */
export const ManualOrderDiagnosisSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    display: z.string().trim().min(1).max(240).optional(),
})
    .strict();
/** Why the order is placed, in one line. Never the visit note's text. */
export const ManualOrderIndicationSchema = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^[^\r\n]+$/u, "The indication is one line");
/**
 * A test the physician ticked on a form or typed on an "Other tests" line, as the form resolved
 * it, so a resumed form ticks the same boxes. `fromOrderSet` marks a test the order's set ticked,
 * which comes off again with the set.
 */
export const ManualOrderLabTickSchema = z
    .object({
    test: z
        .object({
        code: z.string().trim().min(1).max(80),
        codeSystem: z.enum(["LOINC", "local", "RadLex", "free_text"]),
        display: z.string().trim().min(1).max(240),
        /** The reviewed field path the box or line matched by. */
        sourcePath: z.string().trim().min(1).max(240).optional(),
    })
        .strict(),
    fromOrderSet: z.boolean(),
})
    .strict();
/**
 * A study box the physician ticked, by the box's key (study, side and contrast). `replaces` is the
 * study it put another contrast in place of, as the physician read it.
 */
export const ManualOrderImagingTickSchema = z
    .object({
    key: z.string().min(1).max(1_000),
    replaces: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/** What an imaging order says beside its studies. */
export const ManualOrderImagingDetailsSchema = z
    .object({
    /** The question the study should answer. */
    clinicalQuestion: z.string().trim().min(1).max(4_000).optional(),
    /** The physician confirmed kidney function was reviewed, for IV contrast. */
    kidneyFunctionReviewed: z.boolean().optional(),
})
    .strict();
const ManualOrderDraftSharedShape = {
    /** Values typed on the forms, keyed `<documentId>:<fieldId>`. */
    manualValues: ManualOrderManualValuesSchema.default({}),
    diagnosis: ManualOrderDiagnosisSchema.optional(),
    indication: ManualOrderIndicationSchema.optional(),
};
/** A saved lab order: the order as previews send it (`lab`), and the boxes and lines behind it. */
export const ManualOrderDraftLabIntentSchema = z
    .object({
    kind: z.literal("lab"),
    lab: ManualOrderLabIntentSchema,
    ticks: z.array(ManualOrderLabTickSchema).max(MANUAL_ORDER_LIMITS.labTests).default([]),
    ...ManualOrderDraftSharedShape,
})
    .strict();
/** A saved imaging order: the order as previews send it (`imaging`), its boxes and its details. */
export const ManualOrderDraftImagingIntentSchema = z
    .object({
    kind: z.literal("imaging"),
    imaging: ManualOrderImagingIntentSchema,
    ticks: z
        .array(ManualOrderImagingTickSchema)
        .max(MANUAL_ORDER_LIMITS.imagingStudies)
        .default([]),
    details: ManualOrderImagingDetailsSchema.optional(),
    ...ManualOrderDraftSharedShape,
})
    .strict();
/** A saved document: the values typed on it. */
export const ManualOrderDraftDocumentIntentSchema = z
    .object({
    kind: z.literal("document"),
    ...ManualOrderDraftSharedShape,
})
    .strict();
const draftIntentEncoder = new TextEncoder();
/**
 * Everything a "+" order holds before it is signed, as the browser keeps it: the order part
 * (tests or studies, diagnosis codes, set, declines), the ticked boxes and typed lines behind it,
 * the values typed on the forms, the order's diagnosis and indication, and imaging details. The
 * picked form and the order set travel beside it. At most `draftIntentBytes` as JSON.
 */
export const ManualOrderDraftIntentSchema = z
    .discriminatedUnion("kind", [
    ManualOrderDraftLabIntentSchema,
    ManualOrderDraftImagingIntentSchema,
    ManualOrderDraftDocumentIntentSchema,
])
    .superRefine((intent, context) => {
    if (draftIntentEncoder.encode(JSON.stringify(intent)).length >
        MANUAL_ORDER_LIMITS.draftIntentBytes) {
        context.addIssue({
            code: "custom",
            message: "This order holds too much to save; shorten what is typed on the forms",
        });
    }
    const diagnosisCodes = intent.kind === "lab"
        ? intent.lab.diagnosisCodes
        : intent.kind === "imaging"
            ? intent.imaging.diagnosisCodes
            : undefined;
    if (intent.diagnosis !== undefined &&
        diagnosisCodes !== undefined &&
        !diagnosisCodes.some((code) => code.toUpperCase() === intent.diagnosis?.code.toUpperCase())) {
        context.addIssue({
            code: "custom",
            path: ["diagnosis", "code"],
            message: "The order's diagnosis is one of its diagnosis codes",
        });
    }
    if (intent.kind === "lab") {
        const tests = new Set();
        for (const [index, test] of intent.lab.tests.entries()) {
            const key = `${test.codeSystem}\0${test.code}`;
            if (tests.has(key)) {
                context.addIssue({
                    code: "custom",
                    path: ["lab", "tests", index],
                    message: "An order cannot list the same coded test twice",
                });
            }
            tests.add(key);
        }
        const ticks = new Set();
        for (const [index, { test }] of intent.ticks.entries()) {
            const key = `${test.codeSystem.toUpperCase()}|${test.code.toUpperCase()}`;
            if (ticks.has(key)) {
                context.addIssue({
                    code: "custom",
                    path: ["ticks", index],
                    message: "A test is ticked once",
                });
            }
            ticks.add(key);
        }
    }
    if (intent.kind === "imaging") {
        const keys = new Set();
        for (const [index, tick] of intent.ticks.entries()) {
            if (keys.has(tick.key)) {
                context.addIssue({
                    code: "custom",
                    path: ["ticks", index],
                    message: "A study box is ticked once",
                });
            }
            keys.add(tick.key);
        }
    }
});
export const ManualOrderDraftStatusSchema = z.enum(["draft", "signed", "discarded"]);
/**
 * A "+" document signed: its standalone selection, sealed under the physician's document-action
 * receipt. The database fills in the receipt when the request leaves it out.
 */
export const ManualOrderDocumentOutcomeSchema = z
    .object({
    kind: z.literal("document"),
    selectionId: IdSchema,
    receiptId: IdSchema.optional(),
})
    .strict();
/**
 * A "+" lab order placed, as Mark signed names it: its lab order action in the bundle the
 * physician accepted. For a lab order, signed means placed: the requisition may still be unsigned
 * (the order's `order.sealed`), and is signed from the placed order, never by ordering again.
 */
export const MarkManualOrderLabSignedOutcomeSchema = z
    .object({
    kind: z.literal("lab"),
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
})
    .strict();
/** A "+" lab order placed, as recorded: also the approval receipt the database found for it. */
export const ManualOrderLabOutcomeSchema = MarkManualOrderLabSignedOutcomeSchema.extend({
    approvalReceiptId: IdSchema,
}).strict();
/** Imaging orders use the same accepted-action outcome and leave the receipt to the database. */
export const MarkManualOrderImagingSignedOutcomeSchema = MarkManualOrderLabSignedOutcomeSchema.extend({
    kind: z.literal("imaging"),
});
export const ManualOrderImagingOutcomeSchema = MarkManualOrderImagingSignedOutcomeSchema.extend({
    approvalReceiptId: IdSchema,
}).strict();
/**
 * What signing a "+" order produced, as recorded, checked by the database before the order is
 * marked signed: a document's sealed selection, or a diagnostic order's accepted action.
 */
export const ManualOrderDraftOutcomeSchema = z.discriminatedUnion("kind", [
    ManualOrderDocumentOutcomeSchema,
    ManualOrderLabOutcomeSchema,
    ManualOrderImagingOutcomeSchema,
]);
/** What signing produced, as Mark signed sends it: a lab order leaves its receipt to the database. */
export const MarkManualOrderDraftSignedOutcomeSchema = z.discriminatedUnion("kind", [
    ManualOrderDocumentOutcomeSchema,
    MarkManualOrderLabSignedOutcomeSchema,
    MarkManualOrderImagingSignedOutcomeSchema,
]);
/**
 * Where a saved lab or imaging "+" order stands once it has been prepared, as the visit's list
 * reads it: the latest attempt whose order is not expired. `runId` is that attempt's agent run,
 * `bundleId` the order's bundle and `actionId` its lab or imaging order action, each with its
 * version and state (the states of the action workflow). `approvalReceiptId` and `acceptedAt`
 * come once the physician signed the order, which places it. `selectionId` is the latest form set
 * prepared for it, and `sealed` whether every form in that set is signed. Optional keys are left
 * out, never null.
 */
export const ManualOrderLaneSchema = z
    .object({
    attempt: z.number().int().min(1).max(MANUAL_ORDER_MAX_ATTEMPT),
    runId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    bundleContentHash: Sha256Schema,
    bundleState: ActionStatusSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    actionState: ActionStatusSchema,
    approvalReceiptId: IdSchema.optional(),
    acceptedAt: IsoDateTimeSchema.optional(),
    selectionId: IdSchema.optional(),
    sealed: z.boolean(),
})
    .strict()
    .superRefine((lane, context) => {
    if ((lane.approvalReceiptId === undefined) !== (lane.acceptedAt === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["acceptedAt"],
            message: "A placed order says when it was placed, and only a placed one does",
        });
    }
    if (lane.sealed && lane.selectionId === undefined) {
        context.addIssue({
            code: "custom",
            path: ["sealed"],
            message: "Only forms that were prepared can be signed",
        });
    }
});
/** The rules a saved order and a Save share: kind, set and imaging facility follow the intent. */
function checkDraftShape(draft, context) {
    if (draft.intent.kind !== draft.kind) {
        context.addIssue({
            code: "custom",
            path: ["intent", "kind"],
            message: "The intent is of the order's own kind",
        });
    }
    const intentSet = draft.intent.kind === "lab" ? draft.intent.lab.orderSet : undefined;
    if (draft.orderSet !== undefined && draft.kind !== "lab") {
        context.addIssue({
            code: "custom",
            path: ["orderSet"],
            message: "Only a lab order carries an order set",
        });
    }
    else if (draft.orderSet?.id !== intentSet?.id || draft.orderSet?.name !== intentSet?.name) {
        context.addIssue({
            code: "custom",
            path: ["orderSet"],
            message: "The order set is the one the lab order names",
        });
    }
    const destination = draft.intent.kind === "imaging" ? draft.intent.imaging.destination : undefined;
    if (destination !== undefined && !sameManualOrderForm(destination.form, draft.form)) {
        context.addIssue({
            code: "custom",
            path: ["intent", "imaging", "destination", "form"],
            message: "The imaging facility must print the picked form",
        });
    }
}
/** A saved "+" order as the server returns it. `mine` is whether the caller saved it. */
export const ManualOrderDraftSchema = z
    .object({
    id: ManualOrderIdSchema,
    encounterId: IdSchema,
    patientId: IdSchema,
    kind: ManualOrderKindSchema,
    form: ManualOrderFormRefSchema,
    orderSet: LabOrderSetRefSchema.optional(),
    intent: ManualOrderDraftIntentSchema,
    status: ManualOrderDraftStatusSchema,
    outcome: ManualOrderDraftOutcomeSchema.optional(),
    /**
     * Lab and imaging orders only, once prepared: where the order stands (ManualOrderLaneSchema).
     * Absent until a preparation produced an order that is not expired.
     */
    order: ManualOrderLaneSchema.optional(),
    /**
     * Listed only, lab and imaging orders: the highest attempt ever started, whatever became of
     * it. The next preparation uses a higher one, so it never repeats the key of an attempt that
     * stopped before it prepared anything.
     */
    lastAttempt: z.number().int().min(1).max(MANUAL_ORDER_MAX_ATTEMPT).optional(),
    version: z.number().int().positive(),
    createdBy: IdSchema,
    mine: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    signedAt: IsoDateTimeSchema.optional(),
    /**
     * Listed only: a document still open whose form is already signed, under its creator's own
     * signature, for exactly this order (signing finished, recording it did not). Recording that
     * selection is all that is left: it is never signed a second time.
     */
    signedSelectionId: IdSchema.optional(),
})
    .strict()
    .superRefine((draft, context) => {
    checkDraftShape(draft, context);
    const signed = draft.status === "signed";
    if (signed !== (draft.outcome !== undefined) || signed !== (draft.signedAt !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["outcome"],
            message: "A signed order, and only a signed one, says what signing produced and when",
        });
    }
    if (draft.outcome?.kind === "document" && draft.kind !== "document") {
        context.addIssue({
            code: "custom",
            path: ["outcome", "kind"],
            message: "Only a document is signed as a document",
        });
    }
    if (draft.outcome?.kind === "lab" && draft.kind !== "lab") {
        context.addIssue({
            code: "custom",
            path: ["outcome", "kind"],
            message: "Only a lab order is placed as a lab order",
        });
    }
    if (draft.outcome?.kind === "imaging" && draft.kind !== "imaging") {
        context.addIssue({
            code: "custom",
            path: ["outcome", "kind"],
            message: "Only an imaging order is placed as an imaging order",
        });
    }
    if (draft.order !== undefined && draft.kind === "document") {
        context.addIssue({
            code: "custom",
            path: ["order"],
            message: "A document is signed, never placed as an order",
        });
    }
    if (draft.lastAttempt !== undefined && draft.kind === "document") {
        context.addIssue({
            code: "custom",
            path: ["lastAttempt"],
            message: "A document is never prepared as an order",
        });
    }
    if (draft.lastAttempt !== undefined &&
        draft.order !== undefined &&
        draft.lastAttempt < draft.order.attempt) {
        context.addIssue({
            code: "custom",
            path: ["lastAttempt"],
            message: "The highest attempt started is never below the attempt listed",
        });
    }
    if (draft.signedSelectionId !== undefined &&
        (draft.kind !== "document" || draft.status !== "draft")) {
        context.addIssue({
            code: "custom",
            path: ["signedSelectionId"],
            message: "Only an open document says its form is signed but not recorded",
        });
    }
});
/** A visit's saved and signed "+" orders, every physician's, newest first. */
export const ManualOrderDraftListSchema = z
    .array(ManualOrderDraftSchema)
    .max(MANUAL_ORDER_LIMITS.drafts)
    .refine((drafts) => new Set(drafts.map((draft) => draft.id)).size === drafts.length, "A saved order is listed once");
/**
 * Saves a "+" order on the visit in the route path, under the order id in the path.
 * `expectedVersion` 0 saves a new order; otherwise it is the version this Save replaces. The kind
 * and form of a saved order never change.
 */
export const SaveManualOrderDraftRequestSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema,
    expectedVersion: z.number().int().nonnegative(),
    kind: ManualOrderKindSchema,
    form: ManualOrderFormRefSchema,
    orderSet: LabOrderSetRefSchema.optional(),
    intent: ManualOrderDraftIntentSchema,
})
    .strict()
    .superRefine(checkDraftShape);
/** Discards the caller's own unsigned "+" order at its current version. */
export const DiscardManualOrderDraftRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
/**
 * Marks the caller's own "+" order signed with what signing produced: a document's sealed
 * selection, or the accepted lab order action (the database finds and records its receipt).
 */
export const MarkManualOrderDraftSignedRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    outcome: MarkManualOrderDraftSignedOutcomeSchema,
})
    .strict();
/*
 * Why a "+" request was refused, as `error.detail` beside the plain-words message. The first three
 * mean the picked form can no longer be used as the picker listed it, so the picker's list is out
 * of date and the physician picks again from a fresh one.
 */
/** The picked form was changed, removed, unpublished or taken out of form setup. */
export const MANUAL_ORDER_FORM_UNAVAILABLE = "manual_order_form_unavailable";
/** The clinic's rules leave the picked form out of this order. */
export const MANUAL_ORDER_FORM_EXCLUDED = "manual_order_form_excluded";
/** The request names another kind of order than the picked form's category. */
export const MANUAL_ORDER_FORM_KIND_MISMATCH = "manual_order_form_kind_mismatch";
/** The clinic's rules add a lab or imaging form to a "+" document. */
export const MANUAL_ORDER_DOCUMENT_WITH_ORDER_FORM = "manual_order_document_with_order_form";
/** The clinic's rules suggest tests for a "+" document, which orders nothing. */
export const MANUAL_ORDER_DOCUMENT_SUGGESTS_TESTS = "manual_order_document_suggests_tests";
/** Whether a refusal's `detail` says the picked form must be picked again from a fresh list. */
export function isManualOrderFormRefusal(detail) {
    return (detail === MANUAL_ORDER_FORM_UNAVAILABLE ||
        detail === MANUAL_ORDER_FORM_EXCLUDED ||
        detail === MANUAL_ORDER_FORM_KIND_MISMATCH);
}
/** A picked document form, or a form the clinic's rules add beside it, has no signature box. */
export const MANUAL_ORDER_FORM_UNSIGNABLE = "manual_order_form_unsignable";
/** The saved order changed since it was read (another window saved it): read it again. */
export const MANUAL_ORDER_DRAFT_CHANGED = "manual_order_draft_changed";
/** The saved order is already signed or removed, so it can no longer change. */
export const MANUAL_ORDER_DRAFT_CLOSED = "manual_order_draft_closed";
/** A saved order keeps the kind and form it was first saved with. */
export const MANUAL_ORDER_DRAFT_FORM_FIXED = "manual_order_draft_form_fixed";
/** No saved order of the caller's has this id on this visit. */
export const MANUAL_ORDER_DRAFT_NOT_FOUND = "manual_order_draft_not_found";
/** What signing produced is not this order's: another visit, form, kind or selection. */
export const MANUAL_ORDER_OUTCOME_INVALID = "manual_order_outcome_invalid";
/** The order's document is not sealed under the caller's own signature yet. */
export const MANUAL_ORDER_DOCUMENT_NOT_SIGNED = "manual_order_document_not_signed";
/** The signed document already marks another saved order signed. */
export const MANUAL_ORDER_DOCUMENT_TAKEN = "manual_order_document_taken";
/** A prepared form set can no longer be signed as prepared; prepare it again. */
export const DOCUMENT_SELECTION_NEEDS_PREPARING = "document_selection_needs_preparing";
/*
 * Why a lab or imaging "+" order was refused. Each comes with plain words for the order's row.
 */
/**
 * The prepared order or its forms are no longer exactly the reviewed ones, or the chart changed
 * since they were prepared: prepare it again (a new attempt). Nothing was ordered.
 */
export const MANUAL_ORDER_PREPARE_AGAIN = "manual_order_prepare_again";
/**
 * The order is already placed. Record it, or sign its requisition from the placed order; never
 * prepare it again, which would order it twice.
 */
export const MANUAL_ORDER_ALREADY_PLACED = "manual_order_already_placed";
/**
 * No open saved order of the caller's on this visit matches the order being prepared (its id,
 * kind and picked form): save it first. Nothing was ordered.
 */
export const MANUAL_ORDER_NOT_SAVED = "manual_order_not_saved";
/** The prepared order is not exactly what the physician ticked, so nothing was ordered. */
export const MANUAL_ORDER_NOT_REVIEWED = "manual_order_not_reviewed";
/**
 * Orders at this clinic are drafted by an AI model, which does not draft exactly what was ticked,
 * so ordering from a clinic form is off. Nothing was ordered.
 */
export const MANUAL_ORDER_DRAFTING_UNAVAILABLE = "manual_order_drafting_unavailable";
/** The order was placed, so it can no longer be discarded. */
export const MANUAL_ORDER_PLACED_CANNOT_DISCARD = "manual_order_placed_cannot_discard";
/**
 * The visit has nothing saved on the chart to ground the order on (no saved note, allergy,
 * medication or result). Nothing was ordered.
 */
export const MANUAL_ORDER_NO_CHART_EVIDENCE = "manual_order_no_chart_evidence";
/** The clinic has not switched ordering on, so the order could not be signed. Nothing was ordered. */
export const MANUAL_ORDER_ORDERING_NOT_ENABLED = "manual_order_ordering_not_enabled";
/**
 * A prepared form set picked up again after a reload: the caller's own selection, still current,
 * with every form ready to sign. `selection.contentHash` and the artifacts' exact versions are
 * what signing binds to.
 */
export const PreparedDocumentSelectionSchema = z
    .object({
    selection: DocumentSelectionDecisionSchema,
    artifacts: z.array(GeneratedDocumentArtifactSchema).min(1).max(50),
})
    .strict();
/* ---- Signing a prepared lab or imaging "+" order ---------------------------- */
/** One prepared form exactly as it was reviewed: its generated version and that version's hash. */
export const ManualOrderPreflightArtifactSchema = z
    .object({
    id: IdSchema,
    version: z.number().int().positive(),
    contentHash: Sha256Schema,
})
    .strict();
/**
 * Asked right before a prepared lab or imaging "+" order (the one in the route path) is signed:
 * are its prepared forms still exactly the ones reviewed, filled from the chart as it is now? It
 * names the prepared order (its bundle), its form set (the selection) and every form in that set,
 * each by exact version and hash, and the visit version they were prepared at. A mismatch is
 * refused with MANUAL_ORDER_PREPARE_AGAIN, an order already placed with
 * MANUAL_ORDER_ALREADY_PLACED. Every prepared form is named, at most as many as a preview renders.
 */
export const ManualOrderPreflightRequestSchema = z
    .object({
    siteId: IdSchema,
    kind: z.enum(["lab", "imaging"]),
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    bundleContentHash: Sha256Schema,
    selectionId: IdSchema,
    selectionHash: Sha256Schema,
    artifacts: z
        .array(ManualOrderPreflightArtifactSchema)
        .min(1)
        .max(MANUAL_ORDER_LIMITS.previewForms),
    expectedEncounterVersion: z.number().int().positive(),
})
    .strict()
    .superRefine((request, context) => {
    const ids = new Set(request.artifacts.map((artifact) => artifact.id));
    if (ids.size !== request.artifacts.length) {
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "A prepared form is named once",
        });
    }
});
/** A placed, still-active order of the patient's for the same tests or study. */
export const ManualOrderPossibleDuplicateSchema = z
    .object({
    orderedAt: IsoDateTimeSchema,
    sameVisit: z.boolean(),
})
    .strict();
/**
 * The prepared forms are still the reviewed ones. `possibleDuplicates` are the patient's placed,
 * still-active orders for the same tests or study (at most 20): the physician is warned before
 * signing, never refused.
 */
export const ManualOrderPreflightSchema = z
    .object({
    possibleDuplicates: z.array(ManualOrderPossibleDuplicateSchema).max(20),
})
    .strict();
//# sourceMappingURL=manual-orders.js.map